/**
 * Display helpers. Timestamps are rendered in Indian/Mauritius because that is where the fleet and
 * the operators are, and a console showing UTC forces mental arithmetic during an incident.
 */

const TZ = 'Indian/Mauritius'

const time = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

const dateTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const full = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

export const fmtTime = (v?: string | null) => (v ? time.format(new Date(v)) : '—')
export const fmtDateTime = (v?: string | null) => (v ? dateTime.format(new Date(v)) : '—')
export const fmtFull = (v?: string | null) => (v ? full.format(new Date(v)) : '—')

/** Compact relative age: what an operator actually reads on a status row. */
export function fmtAgo(v?: string | null): string {
  if (!v) return '—'
  const seconds = Math.floor((Date.now() - new Date(v).getTime()) / 1000)
  if (seconds < 0) return 'in ' + fmtDuration(-seconds)
  if (seconds < 10) return 'just now'
  return fmtDuration(seconds) + ' ago'
}

export function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    return m ? `${h}h ${m}m` : `${h}h`
  }
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  return h ? `${d}d ${h}h` : `${d}d`
}

export const fmtNumber = (n?: number | null) => (n === null || n === undefined ? '—' : n.toLocaleString('en-GB'))

export function fmtMoney(amount?: number | null, currency?: string | null) {
  if (amount === null || amount === undefined) return '—'
  return `${currency ?? 'MUR'} ${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Recipients are personal data and the console is often on a shared screen, so numbers are shown
 * partially masked. The full value stays available in the message detail view.
 */
export function maskMsisdn(e164: string) {
  if (e164.length < 7) return e164
  return `${e164.slice(0, 5)}••••${e164.slice(-2)}`
}

/** Maps +CSQ 0..31 (99 = unknown) to 0..4 bars. */
export function rssiBars(rssi?: number | null): number {
  if (rssi === null || rssi === undefined || rssi === 99) return 0
  if (rssi >= 20) return 4
  if (rssi >= 15) return 3
  if (rssi >= 10) return 2
  if (rssi >= 5) return 1
  return 0
}

export const rssiDbm = (rssi?: number | null) =>
  rssi === null || rssi === undefined || rssi === 99 ? null : -113 + 2 * rssi

/** Turns reason_code into something a non-engineer can read. */
export function humanReason(code?: string | null): string {
  if (!code) return '—'
  return code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}
